import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Plus, Play, Edit2, Trash2, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { fmtMoney, fmtNum, networkLabel } from "../lib/api";

function actionBadge(action) {
  const map = {
    increase: "bg-emerald-50 text-emerald-700 border-emerald-200",
    decrease: "bg-amber-50 text-amber-700 border-amber-200",
    pause: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-mono border uppercase ${map[action] || "border-zinc-200"}`}>
      {action}
    </span>
  );
}

function scheduleLabel(r) {
  if (r.schedule_type === "manual" || !r.schedule_type) return "On demand";
  if (r.schedule_type === "interval") return `Every ${r.interval_days || 1}d @ ${r.time_of_day || "09:00"} UTC`;
  if (r.schedule_type === "weekly") {
    const days = (r.weekly_days || []).map((d) => d.slice(0, 3).toUpperCase()).join(",");
    const w = r.weekly_every_n_weeks || 1;
    return `${days || "—"} · every ${w}w @ ${r.time_of_day || "09:00"} UTC`;
  }
  return "—";
}

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // {id, name}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/rules");
      setRules(r.data || []);
    } catch (e) {
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleEnabled = async (rule) => {
    try {
      await api.put(`/rules/${rule.id}`, { ...rule, enabled: !rule.enabled });
      toast.success(`Rule ${!rule.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/rules/${deleteTarget.id}`);
      toast.success(`Rule "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Failed to delete", { description: e.response?.data?.detail || e.message });
    }
  };

  const handlePreview = async (rule) => {
    setPreviewData({ rule, matched: 0, proposals: [] });
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const r = await api.post(`/rules/${rule.id}/evaluate`);
      setPreviewData({ rule, ...r.data });
    } catch (e) {
      toast.error("Preview failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRun = async (rule) => {
    setRunningId(rule.id);
    try {
      const r = await api.post(`/rules/${rule.id}/run`);
      const { created, executed, failed } = r.data;
      toast.success(`Rule ran: ${created} proposals, ${executed} auto-executed, ${failed} failed`);
      load();
    } catch (e) {
      toast.error("Run failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div data-testid="rules-page" className="fade-in">
      <PageHeader
        eyebrow="Automation Logic"
        title="Budget Rules"
        description="Define how budgets adjust based on CPA & conversions. Auto-mode pushes changes immediately; manual queues them for approval."
        actions={
          <Link to="/rules/new">
            <Button data-testid="new-rule-btn" className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs">
              <Plus className="w-4 h-4 mr-2" />
              New rule
            </Button>
          </Link>
        }
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="bg-white border border-zinc-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="rules-table">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-left px-5 py-3 data-label">Name</th>
                  <th className="text-left px-5 py-3 data-label">Action</th>
                  <th className="text-left px-5 py-3 data-label">Network</th>
                  <th className="text-right px-5 py-3 data-label">Target CPA</th>
                  <th className="text-right px-5 py-3 data-label">Adjust %</th>
                  <th className="text-left px-5 py-3 data-label">Schedule</th>
                  <th className="text-left px-5 py-3 data-label">Mode</th>
                  <th className="text-center px-5 py-3 data-label">Enabled</th>
                  <th className="text-right px-5 py-3 data-label">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && !loading && (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-zinc-500 font-mono text-sm">
                      No rules yet — <Link to="/rules/new" className="underline">create your first rule</Link>.
                    </td>
                  </tr>
                )}
                {rules.map((r) => (
                  <tr key={r.id} data-testid={`rule-row-${r.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-zinc-900">{r.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">
                        Lookback {r.lookback_days}d · last run {r.last_run_at ? new Date(r.last_run_at).toLocaleString() : "—"}
                      </div>
                    </td>
                    <td className="px-5 py-3">{actionBadge(r.action)}</td>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className="rounded-none font-mono uppercase text-xs border-zinc-300">
                        {networkLabel(r.network)}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-right font-mono">{fmtMoney(r.target_cpa)}</td>
                    <td className="px-5 py-3 text-right font-mono">{r.action === "pause" ? `>${r.cpa_threshold_pct}%` : `${r.adjustment_pct}%`}</td>
                    <td className="px-5 py-3">
                      <div className="text-xs font-mono text-zinc-700">{scheduleLabel(r)}</div>
                      {r.next_run_at && r.schedule_type !== "manual" && (
                        <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                          Next: {new Date(r.next_run_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-xs font-mono border uppercase ${
                        r.automation_mode === "auto"
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : "bg-zinc-100 text-zinc-700 border-zinc-200"
                      }`}>
                        {r.automation_mode}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <Switch
                        data-testid={`rule-toggle-${r.id}`}
                        checked={r.enabled}
                        onCheckedChange={() => toggleEnabled(r)}
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid={`rule-preview-${r.id}`}
                          variant="ghost"
                          size="sm"
                          className="rounded-none h-8 text-xs"
                          onClick={() => handlePreview(r)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                        </Button>
                        <Button
                          data-testid={`rule-run-${r.id}`}
                          variant="ghost"
                          size="sm"
                          className="rounded-none h-8 text-xs"
                          disabled={!r.enabled || runningId === r.id}
                          onClick={() => handleRun(r)}
                        >
                          <Play className="w-3.5 h-3.5 mr-1" /> Run
                        </Button>
                        <Link to={`/rules/${r.id}/edit`}>
                          <Button data-testid={`rule-edit-${r.id}`} variant="ghost" size="sm" className="rounded-none h-8 text-xs">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                        <Button
                          data-testid={`rule-delete-${r.id}`}
                          variant="ghost"
                          size="sm"
                          className="rounded-none h-8 text-xs text-red-600 hover:text-red-700"
                          onClick={() => setDeleteTarget({ id: r.id, name: r.name })}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>        <DialogContent className="rounded-none max-w-3xl border-zinc-300 max-h-[80vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <div className="data-label">Rule Preview</div>
            <DialogTitle className="font-display tracking-tight uppercase">
              {previewData?.rule?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-zinc-900 text-emerald-300 p-4 font-mono text-xs overflow-x-auto">
            {previewLoading ? (
              <span className="text-zinc-400">{">"} evaluating rule against ThoughtMetric data…</span>
            ) : (
              <>
                <div className="text-zinc-400">{">"} matched: {previewData?.matched || 0} campaign(s)</div>
                {(previewData?.proposals || []).map((p, i) => (
                  <div key={i} className="mt-3 border-t border-zinc-700 pt-2">
                    <div className="text-amber-300">[{p.action.toUpperCase()}]</div>
                    <div className="text-zinc-200">{p.campaign_name}</div>
                    <div className="text-zinc-400">
                      cpa={fmtMoney(p.observed_cpa)} · conv={fmtNum(p.observed_conversions, 1)} · spend={fmtMoney(p.observed_spend)}
                    </div>
                    <div className="text-zinc-500">// {p.reason}</div>
                  </div>
                ))}
                {(previewData?.proposals || []).length === 0 && !previewLoading && (
                  <div className="text-zinc-500 mt-2">{">"} no campaigns match this rule.</div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setPreviewOpen(false)}
              data-testid="preview-close-btn"
            >
              Close
            </Button>
            {previewData?.rule && (
              <Button
                data-testid="preview-run-btn"
                className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                onClick={async () => { await handleRun(previewData.rule); setPreviewOpen(false); }}
              >
                Run rule
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-zinc-300" data-testid="delete-rule-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-mono text-zinc-900">"{deleteTarget?.name}"</span>?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-rule-btn"
              onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
